import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Input, Modal, message, Typography, Empty, Result,
  Card, Row, Col, Tooltip, Dropdown, Select, Segmented, Badge, Alert, Collapse, Spin,
} from 'antd';
import { SearchOutlined, ReloadOutlined, FileTextOutlined, FileTextFilled, ArrowRightOutlined, ShopOutlined, InfoCircleOutlined, CopyOutlined, ThunderboltOutlined, CheckCircleFilled, CloseCircleFilled, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getTraslados,
  getTrasladoById,
  updateTrasladoEstado,
  procesarTraslado,
  getGescomFarmacia,
  ESTADO_PENDIENTE,
  type ReconciliacionTraslado,
  type ProcesarTrasladoResponse,
  type GescomFarmaciaData,
} from '../../api/reconciliacionApi';
import FarmaciaTitularesModal from '../FarmaciaTitularesModal';
import AbrirCasoButton from '../MejoraCU2/AbrirCasoButton';

const { Text } = Typography;
const { TextArea } = Input;

const CRM_PHARMACY_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_PHARMACY_URL_PARAMS = '&idFam=3&idFamCls=15&pag=25&sraccess=1&tmp=616';

const ESTADO_TODOS = -1;

// Estados del traslado — valores no contiguos (0,1,2,3,5). color = Tag AntD, badgeColor = Badge.
const ESTADOS_TRASLADO: { value: number; label: string; color: string; badgeColor: string; dot: string }[] = [
  { value: 0, label: 'Pendiente', color: 'orange', badgeColor: '#fa8c16', dot: '#fa8c16' },
  { value: 1, label: 'Traslado OK', color: 'green', badgeColor: '#52c41a', dot: '#52c41a' },
  { value: 2, label: 'Formato', color: 'blue', badgeColor: '#1890ff', dot: '#1890ff' },
  { value: 3, label: 'Cambio nombre', color: 'purple', badgeColor: '#722ed1', dot: '#722ed1' },
  { value: 5, label: 'Descartado', color: 'default', badgeColor: '#8c8c8c', dot: '#8c8c8c' },
];

const estadoLabels: Record<number, { label: string; color: string }> = Object.fromEntries(
  ESTADOS_TRASLADO.map(e => [e.value, { label: e.label, color: e.color }])
);

const FmtField: React.FC<{ label: string; oldVal?: string; newVal?: string }> = ({ label, oldVal, newVal }) => {
  const changed = (oldVal || '') !== (newVal || '');
  return (
    <div style={{ marginBottom: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Text style={{ flex: 1 }}>{oldVal || '-'}</Text>
        <ArrowRightOutlined style={{ color: '#999', fontSize: 12 }} />
        <Text strong style={{ flex: 1, backgroundColor: changed ? '#fff7e6' : undefined, padding: changed ? '2px 6px' : undefined, borderRadius: 4 }}>
          {newVal || '-'}
        </Text>
      </div>
    </div>
  );
};

interface TrasladosTabProps {
  selectedRecIds?: number[];
}

const TrasladosTab: React.FC<TrasladosTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionTraslado[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionTraslado[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [searchText, setSearchText] = useState('');
  const [estadoCounts, setEstadoCounts] = useState<Record<number, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Tratamiento por lotes (solo en vista de pendientes)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchEstado, setBatchEstado] = useState<number | undefined>(undefined);
  const [batchLoading, setBatchLoading] = useState(false);

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionTraslado | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [showTitularesModal, setShowTitularesModal] = useState(false);

  // Procesamiento en CRM (Aura)
  const [procesando, setProcesando] = useState(false);
  const [procesarResult, setProcesarResult] = useState<ProcesarTrasladoResponse | null>(null);
  // Dirección actual de la farmacia en Gescom-AQ (para la cabecera del modal)
  const [gescomFarmacia, setGescomFarmacia] = useState<GescomFarmaciaData | null>(null);
  const [loadingGescom, setLoadingGescom] = useState(false);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getTraslados(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getTraslados(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const results = await Promise.all(ESTADOS_TRASLADO.map(e => fetchByEstado(e.value)));
      const counts: Record<number, number> = {};
      ESTADOS_TRASLADO.forEach((e, i) => { counts[e.value] = results[i].length; });
      setEstadoCounts(counts);
    } catch { /* silently fail */ }
    finally { setLoadingCounts(false); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedEstado, selectedRecIds]);

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  const applySearch = (items: ReconciliacionTraslado[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(t =>
      (t.farmaciaGescom?._Name || '').toLowerCase().includes(lower) ||
      (t.direccion || '').toLowerCase().includes(lower) ||
      (t.localidad || '').toLowerCase().includes(lower) ||
      (t.provincia || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateTrasladoEstado(id, estadoId, notasGestion);
      message.success('Traslado actualizado correctamente');
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar el traslado');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = (item: ReconciliacionTraslado) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setProcesarResult(null);
    setGescomFarmacia(null);
    setModalVisible(true);
    if (item.idFarmaciaGS) {
      setLoadingGescom(true);
      getGescomFarmacia(item.idFarmaciaGS)
        .then(setGescomFarmacia)
        .catch(() => setGescomFarmacia(null))
        .finally(() => setLoadingGescom(false));
    }
  };

  const handleProcesar = (item: ReconciliacionTraslado) => {
    Modal.confirm({
      title: 'Aplicar traslado en Gescom',
      content: `Se actualizará la dirección de la farmacia en Gescom a: "${item.direccion || '(sin dirección)'}". Esta acción no se puede deshacer. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setProcesando(true);
        try {
          const result = await procesarTraslado(item.id);
          setProcesarResult(result);
          if (result.error !== 0) {
            message.error(`Error: ${result.mensajeError}`);
          } else {
            message.success('Traslado aplicado correctamente en Gescom');
            // La dirección en Gescom ha cambiado: refrescar la cabecera
            if (item.idFarmaciaGS) {
              setLoadingGescom(true);
              getGescomFarmacia(item.idFarmaciaGS)
                .then(setGescomFarmacia)
                .catch(() => { /* mantener la anterior */ })
                .finally(() => setLoadingGescom(false));
            }
          }
          // Refrescar el caso (estado, notasProcesamiento, fechaProcesamiento)
          const refreshed = await getTrasladoById(item.id);
          setSelectedItem(refreshed);
          setEditEstadoId(refreshed.estadoId);
          loadData();
          loadCounts();
        } catch {
          message.error('Error al comunicar con Gescom');
        } finally {
          setProcesando(false);
        }
      },
    });
  };

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
    setSearchText('');
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
  };

  const handleRefresh = async () => {
    await Promise.all([loadData(), loadCounts()]);
  };

  // Estados aplicables en lote (se omiten Pendiente y Traslado OK)
  const BATCH_ESTADOS = ESTADOS_TRASLADO.filter(e => e.value === 2 || e.value === 3 || e.value === 5);

  const doBatchApply = async () => {
    setBatchLoading(true);
    try {
      await Promise.all(selectedRowKeys.map(id => updateTrasladoEstado(Number(id), batchEstado!)));
      message.success(`${selectedRowKeys.length} traslados actualizados`);
      setSelectedRowKeys([]);
      setBatchEstado(undefined);
      await Promise.all([loadData(), loadCounts()]);
    } catch {
      message.error('Error al actualizar los traslados en lote');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchApply = () => {
    if (batchEstado === undefined || selectedRowKeys.length === 0) return;
    const estadoLabel = ESTADOS_TRASLADO.find(e => e.value === batchEstado)?.label || '';
    Modal.confirm({
      title: 'Confirmar cambio de estado',
      content: `Se marcarán ${selectedRowKeys.length} traslados como "${estadoLabel}". ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: doBatchApply,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const columns: ColumnsType<ReconciliacionTraslado> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      render: (_, r) => r.farmaciaGescom?._Name || `CU2 ID ${r.idFarmaciaCU2}`,
      ellipsis: true,
      width: 260,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 130,
    },
    {
      title: 'Dir. anterior',
      key: 'dirAnterior',
      render: (_, r) => `${r.direccionAnterior || ''}, ${r.localidadAnterior || ''}`.replace(/^, |, $/g, '') || '-',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Dir. nueva',
      key: 'dirNueva',
      render: (_, r) => `${r.direccion || ''}, ${r.localidad || ''}`.replace(/^, |, $/g, '') || '-',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      width: 110,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number) => {
        const est = estadoLabels[val] || { label: `Estado ${val}`, color: 'default' };
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextFilled style={{ color: '#1890ff' }} /></Tooltip>
      ) : (
        <FileTextOutlined style={{ color: '#d9d9d9' }} />
      ),
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar traslados"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Buscar farmacia..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          size="large"
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={Object.values(estadoCounts).reduce((a, b) => a + b, 0)}
                    style={{ backgroundColor: '#595959' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            ...ESTADOS_TRASLADO.map(e => ({
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{e.label}</span>
                  <Badge
                    count={estadoCounts[e.value] || 0}
                    style={{ backgroundColor: e.badgeColor }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: e.value,
            })),
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>Actualizar</Button>
      </div>

      {/* Barra de tratamiento por lotes (solo en pendientes y con selección) */}
      {selectedEstado === ESTADO_PENDIENTE && selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: 12, padding: '10px 16px',
          background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 6,
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Text strong>{selectedRowKeys.length} seleccionados</Text>
          <Select
            placeholder="Marcar como..."
            value={batchEstado}
            onChange={setBatchEstado}
            style={{ width: 200 }}
            options={BATCH_ESTADOS.map(e => ({
              value: e.value,
              label: <span style={{ color: e.dot }}>● {e.label}</span>,
            }))}
          />
          <Button type="primary" disabled={batchEstado === undefined} loading={batchLoading} onClick={handleBatchApply}>
            Aceptar
          </Button>
          <Button onClick={() => { setSelectedRowKeys([]); setBatchEstado(undefined); }}>Cancelar</Button>
        </div>
      )}

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(t => ({ ...t, key: t.id }))}
        columns={columns}
        rowSelection={selectedEstado === ESTADO_PENDIENTE ? rowSelection : undefined}
        loading={loading}
        pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        size="middle"
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="No hay traslados" /> }}
      />

      {/* Modal de detalle con comparativa */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><ShopOutlined style={{ marginRight: 8 }} />Farmacia. Dirección actual en Gescom:</span>
            {!selectedItem.idFarmaciaGS ? (
              <Text type="secondary" style={{ fontWeight: 'normal' }}>(sin vincular)</Text>
            ) : loadingGescom ? (
              <Spin size="small" />
            ) : (
              <Tooltip title="Abrir ficha de la farmacia en el CRM">
                <a
                  href={`${CRM_PHARMACY_URL}${selectedItem.idFarmaciaGS}${CRM_PHARMACY_URL_PARAMS}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    color: '#389e0d',
                    fontWeight: 600,
                    padding: '2px 10px',
                    borderRadius: 4,
                  }}
                >
                  {[gescomFarmacia?.direccion, gescomFarmacia?.localidad].filter(Boolean).join(', ') || '—'}
                </a>
              </Tooltip>
            )}
          </span>
        ) : ''}
        width={750}
      >
        {selectedItem && (
          <div>
            {/* Identificadores de la farmacia */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <Tag color="geekblue">CU2: {selectedItem.idFarmaciaCU2}</Tag>
              {selectedItem.idFarmaciaGS ? (
                <Tag color="green">Gescom: {selectedItem.idFarmaciaGS}</Tag>
              ) : (
                <Tag color="warning">Gescom: sin vincular</Tag>
              )}
            </div>

            {/* Aviso: farmacia no vinculada */}
            {!selectedItem.idFarmaciaGS && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Farmacia no vinculada a Gescom"
                description="No se puede abrir la ficha del CRM ni consultar titulares hasta que la farmacia esté vinculada (idFarmaciaGS). Vincúlela antes de aplicar el traslado en el CRM."
              />
            )}

            {/* Comparativa de direcciones */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Dropdown
                        menu={{
                          items: [{
                            key: 'gs',
                            label: <span>ID Gescom: <strong>{selectedItem.idFarmaciaGS}</strong> <CopyOutlined style={{ color: '#999', marginLeft: 4 }} /></span>,
                            onClick: () => { navigator.clipboard.writeText(String(selectedItem.idFarmaciaGS || '')); },
                          }],
                        }}
                        trigger={['click']}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          Dirección previa
                        </span>
                      </Dropdown>
                      <Tooltip title="Ver titulares actuales en Gescom">
                        <InfoCircleOutlined
                          style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }}
                          onClick={() => setShowTitularesModal(true)}
                        />
                      </Tooltip>
                    </div>
                  }
                  size="small"
                  bordered
                  style={{ background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: '#d9d9d9' }}
                  headStyle={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                >
                  <div>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>Dirección</Text><div>{selectedItem.direccionAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>CP</Text><div>{selectedItem.cpAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Localidad</Text><div>{selectedItem.localidadAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Provincia</Text><div>{selectedItem.provinciaAnterior || '-'}</div></div>
                  </div>
                </Card>
              </Col>
              <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowRightOutlined style={{ fontSize: 20, color: '#1890ff' }} />
              </Col>
              <Col span={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Nueva dirección</span>
                      <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                        {new Date(selectedItem.fechaCreacion).toLocaleDateString('es-ES')}
                      </Text>
                    </div>
                  }
                  size="small"
                  bordered
                  style={{ background: '#fff7e6', borderColor: '#ffd591', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                  headStyle={{ backgroundColor: '#fff7e6', fontWeight: 'bold', borderBottom: '1px solid #ffd591' }}
                >
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Dirección</Text>
                    <div style={{ backgroundColor: selectedItem.direccion !== selectedItem.direccionAnterior ? '#fff1b8' : undefined, padding: '2px 4px', borderRadius: 4 }}>
                      {selectedItem.direccion || '-'}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>CP</Text>
                    <div style={{ backgroundColor: selectedItem.cp !== selectedItem.cpAnterior ? '#fff1b8' : undefined, padding: '2px 4px', borderRadius: 4 }}>
                      {selectedItem.cp || '-'}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Localidad</Text>
                    <div style={{ backgroundColor: selectedItem.localidad !== selectedItem.localidadAnterior ? '#fff1b8' : undefined, padding: '2px 4px', borderRadius: 4 }}>
                      {selectedItem.localidad || '-'}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Provincia</Text>
                    <div style={{ backgroundColor: selectedItem.provincia !== selectedItem.provinciaAnterior ? '#fff1b8' : undefined, padding: '2px 4px', borderRadius: 4 }}>
                      {selectedItem.provincia || '-'}
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Comentario */}
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
            <TextArea
              value={editNotas}
              onChange={e => setEditNotas(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="Añadir comentario..."
              style={{ marginBottom: 16 }}
            />

            {/* Panel de resultado del procesamiento */}
            {(procesarResult || selectedItem.notasProcesamiento) && (
              <Collapse
                size="small"
                defaultActiveKey={procesarResult ? ['1'] : []}
                style={{ marginBottom: 16 }}
                items={[{
                  key: '1',
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><ThunderboltOutlined style={{ marginRight: 8 }} />Resultado del procesamiento</span>
                      {selectedItem.fechaProcesamiento && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(selectedItem.fechaProcesamiento).toLocaleDateString('es-ES')}</Text>}
                    </div>
                  ),
                  children: (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      {(procesarResult ? procesarResult.error === 0 : !selectedItem.notasProcesamiento?.startsWith('Error al procesar'))
                        ? <CheckCircleFilled style={{ color: '#52c41a', marginTop: 4 }} />
                        : <CloseCircleFilled style={{ color: '#f5222d', marginTop: 4 }} />}
                      <Text style={{ whiteSpace: 'pre-wrap' }}>
                        {procesarResult
                          ? (procesarResult.error !== 0 ? procesarResult.mensajeError : (procesarResult.resultadoCasoLimpio || 'Dirección actualizada en Gescom'))
                          : selectedItem.notasProcesamiento}
                      </Text>
                    </div>
                  ),
                }]}
              />
            )}

            {/* Acciones: info + estado selector + Guardar + Procesar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tooltip title="Guardar solo registra el estado de seguimiento. Procesar aplica el cambio de dirección en Gescom (Aura) y, si tiene éxito, marca el traslado como Traslado OK.">
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }} />
              </Tooltip>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 180 }}
              >
                {ESTADOS_TRASLADO.map(e => (
                  <Select.Option key={e.value} value={e.value}>
                    <span style={{ color: e.dot }}>● {e.label}</span>
                  </Select.Option>
                ))}
              </Select>
              <Button
                icon={<SaveOutlined />}
                loading={actionLoading}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
              >
                Guardar
              </Button>
              {selectedItem.estadoId === ESTADO_PENDIENTE && (
                <Tooltip title={!selectedItem.idFarmaciaGS ? 'La farmacia no está vinculada a Gescom' : undefined}>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={procesando}
                    disabled={!selectedItem.idFarmaciaGS}
                    onClick={() => handleProcesar(selectedItem)}
                  >
                    Procesar...
                  </Button>
                </Tooltip>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <AbrirCasoButton
                  prefill={{
                    entidadTipo: 'Traslado',
                    entidadRefId: selectedItem.id,
                    reconciliacionId: selectedItem.reconciliacionId,
                    titulo: `Revisión traslado #${selectedItem.id}`,
                    descripcion:
                      `Farmacia: ${selectedItem.farmaciaGescom?._Name ?? '—'}\n` +
                      `id_farmacia (CU2): ${selectedItem.idFarmaciaCU2}\n` +
                      `id_farmacia_gs (Gescom): ${selectedItem.idFarmaciaGS ?? '—'}`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal titulares actuales en Gescom */}
      {selectedItem && (
        <FarmaciaTitularesModal
          visible={showTitularesModal}
          onClose={() => setShowTitularesModal(false)}
          farmaciaId={selectedItem.idFarmaciaGS || 0}
          farmaciaName={selectedItem.farmaciaGescom?._Name || 'Sin datos'}
          userId={1}
        />
      )}
    </div>
  );
};

export default TrasladosTab;
